import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { keys, PREF_ONE_CLICK_SELL_ENABLED, type MarketGroup } from "@/lib/queries";
import { openOrdersForMarketQuery, OPEN_ORDERS_SCAN } from "@/lib/sellAvailable";
import { MarketDetailView } from "@/components/markets/MarketDetailView";
import type { OrderVerdict } from "@/hooks/useOrderStatusStream";
import type { MarketItem, TickerSnapshot, DepthSnapshot, PositionItem, OrderItem, PortfolioActivityItem, PageInfo } from "@/lib/types";
import { buildHead } from "@/lib/seo";

// The market-detail activity panel (MarketActivity) reads each tab through
// useOffsetFeed, so its cache keys are the *Feed variants (page size 5 + the
// tab's server sort), NOT the plain keys the portfolio page seeds. Mirror those
// here so all three tabs (Position / Open Order / History) render from the
// mocks. Sort columns match MarketActivity.SORT_COL; newest-first is the default.
const MARKET_PAGE = 5;
const POS_SORT = { by: "opened_at", dir: "desc" } as const;
const ORDER_SORT = { by: "created_at", dir: "desc" } as const;
const HIST_SORT = { by: "occurred_at", dir: "desc" } as const;

// Dev-only preview of the REAL market-detail screen (MarketDetailView) with the
// query cache seeded so it renders without a backend. Exercises the full
// trade-panel + activity-panel + sell-seed flow. Not linked from the app.
const MOCK_MARKET = {
  market_id: "m1",
  name: "Brazil to win",
  outcomes: [
    { outcome_id: "y", market_id: "m1", code: "YES", name: "Yes", display_order: 0 },
    { outcome_id: "n", market_id: "m1", code: "NO", name: "No", display_order: 1 },
  ],
  amount_precision: 0,
} as unknown as MarketItem;

const MOCK_GROUP: MarketGroup = {
  id: "m1",
  title: "Which team is gonna win the World Cup 2026?",
  category_id: "sports",
  category_name: "Sports",
  event_id: "m1",
  closes_at: new Date(Date.now() + 30 * 86_400_000).toISOString(),
  markets: [MOCK_MARKET],
  multi: false,
  kind: "binary",
};

const MOCK_TICKER = {
  market_id: "m1",
  best_bid: "6100",
  best_ask: "6200",
  mid_price: "6150",
  last_price: "6150",
  open_price: "5800",
} as unknown as TickerSnapshot;

const MOCK_DEPTH = {
  market_id: "m1",
  sequence: 1,
  bids: [
    { price: "6100", size: "120" },
    { price: "6050", size: "80" },
    { price: "6000", size: "200" },
  ],
  asks: [
    { price: "6200", size: "90" },
    { price: "6250", size: "150" },
    { price: "6300", size: "70" },
  ],
  generated_at: "",
} as unknown as DepthSnapshot;

// Scoped History feed for m1 — a buy (derives ⚡708 from 12 × ⚡59), a sell
// (realized proceeds), and a redeem (payout, no shares subline). Renders the
// shared HistoryActivityCard the portfolio tab uses.
const histPage: PageInfo = { total: 6, limit: 50, offset: 0, has_more: false };
// One row per History activity state so every filter bucket has something to
// match: buy / sell / redeem (won is its claimed form) / close (loser dismiss)
// / placed (resting order, gateway-synthesized from a live OPEN order) /
// cancel (terminal order_history status). See META + HISTORY_BUCKETS.
const MOCK_ACTIVITY = [
  { key: "h1", source: "position_event", kind: "INCREASED", activity: "buy", market_id: "m1", outcome_id: "y", delta_qty: "12", fill_price: "5900", ref_id: "pp1", occurred_at: new Date(Date.now() - 3 * 86_400_000).toISOString() },
  { key: "h2", source: "position_event", kind: "DECREASED", activity: "sell", market_id: "m1", outcome_id: "y", value_minor: "74400", delta_qty: "12", fill_price: "6200", ref_id: "pp1", occurred_at: new Date(Date.now() - 2 * 86_400_000).toISOString() },
  { key: "h3", source: "position_event", kind: "REDEEMED", activity: "redeem", market_id: "m1", outcome_id: "y", value_minor: "120000", ref_id: "pp1", occurred_at: new Date(Date.now() - 86_400_000).toISOString() },
  // Loser-side dismiss: kind=CLOSED, no trade_id — the guard renders it "Closed".
  { key: "h4", source: "position_event", kind: "CLOSED", activity: "sell", market_id: "m1", outcome_id: "n", ref_id: "pp3", occurred_at: new Date(Date.now() - 43_200_000).toISOString() },
  // Resting limit order → "Placed" (Buy 8 at ⚡55, no cash moved). cancel →
  // "Canceled" (Buy 5 at ⚡67). Both carry the intent-space order snapshot.
  { key: "h5", source: "order_history", kind: "OPEN", activity: "placed", market_id: "m1", outcome_id: "y", side: "buy", order_qty: "8", order_price: "5500", amount_precision: 0, ref_id: "o9", occurred_at: new Date(Date.now() - 30 * 60_000).toISOString() },
  { key: "h6", source: "order_history", kind: "CANCELED", activity: "cancel", market_id: "m1", outcome_id: "n", side: "buy", order_qty: "5", order_price: "6700", amount_precision: 0, ref_id: "o8", occurred_at: new Date(Date.now() - 20 * 60_000).toISOString() },
] as unknown as PortfolioActivityItem[];

// Three resting open orders on m1 so the Open Order tab renders the shared
// card: a GTC Yes buy (Expiration "Never"), a partially-filled GTD No buy
// (3/8 filled, dated expiry), and a resting GTC Yes SELL whose 12 remaining
// tickets net the trade panel's SELL Available down to 25 of the 37 held
// (the client mirror of the server's ORDER_000128 coverage check). Status
// must be in OpenOrderCard.CANCELLABLE.
const ordersPage: PageInfo = { total: 3, limit: MARKET_PAGE, offset: 0, has_more: false };
const MOCK_ORDERS = [
  { order_id: "o1", market_id: "m1", outcome_id: "y", side: "buy", price: "5900", intent_price: "5900", quantity: "10", remaining_quantity: "10", filled_quantity: "0", tif: "GTC", status: "OPEN", amount_precision: 0, created_at: new Date(Date.now() - 3_600_000).toISOString() },
  { order_id: "o2", market_id: "m1", outcome_id: "n", side: "buy", price: "3500", intent_price: "3500", quantity: "8", remaining_quantity: "5", filled_quantity: "3", tif: "GTD", status: "PARTIALLY_FILLED", expire_at: new Date(Date.now() + 3 * 86_400_000).toISOString(), amount_precision: 0, created_at: new Date(Date.now() - 7_200_000).toISOString() },
  { order_id: "o3", market_id: "m1", outcome_id: "y", side: "sell", price: "6500", intent_side: "sell", intent_price: "6500", quantity: "12", remaining_quantity: "12", filled_quantity: "0", tif: "GTC", status: "OPEN", amount_precision: 0, created_at: new Date(Date.now() - 1_800_000).toISOString() },
] as unknown as OrderItem[];

// --- Multi-outcome story (?multi=1) -------------------------------------
// Five answers, ranked by chance (Spain leads). Spain carries two open
// positions (YES + NO) and two resting orders; Brazil one position; Italy a
// REDEEMABLE win + a resting order — so the answer rows show the solid +
// dashed holdings chips (mobile footer, desktop under the inline book), and
// the Position tab mixes open rows with a result card. Exercises the
// answer-list/choice-view split and the URL-pushed mobile navigation.
const TEAMS = [
  { mid: "t1", name: "Spain", ask: "2000", bid: "1900" },
  { mid: "t2", name: "Brazil", ask: "1800", bid: "1700" },
  { mid: "t3", name: "Italy", ask: "1500", bid: "1400" },
  { mid: "t4", name: "Germany", ask: "1200", bid: "1100" },
  { mid: "t5", name: "France", ask: "900", bid: "800" },
] as const;
const MULTI_IDS = TEAMS.map((t) => t.mid);

const MULTI_MARKETS = TEAMS.map(({ mid, name }) => ({
  market_id: mid,
  name,
  outcomes: [
    { outcome_id: `${mid}y`, market_id: mid, code: "YES", name: "Yes", display_order: 0 },
    { outcome_id: `${mid}n`, market_id: mid, code: "NO", name: "No", display_order: 1 },
  ],
  amount_precision: 0,
})) as unknown as MarketItem[];

const MULTI_GROUP: MarketGroup = {
  id: "e1",
  title: "Which team is gonna win the World Cup 2026…?",
  category_id: "sports",
  category_name: "Sports",
  event_id: "e1",
  closes_at: new Date(Date.now() + 30 * 86_400_000).toISOString(),
  markets: MULTI_MARKETS,
  multi: true,
  kind: "categorical",
};

const MULTI_TICKERS = TEAMS.map(({ mid, ask, bid }) => ({
  market_id: mid,
  best_bid: bid,
  best_bid_size: "100",
  best_ask: ask,
  best_ask_size: "100",
  mid_price: String((Number(ask) + Number(bid)) / 2),
  last_price: ask,
  open_price: "1000",
})) as unknown as TickerSnapshot[];

const multiDepth = (mid: string, ask: number, bid: number) =>
  ({
    market_id: mid,
    sequence: 1,
    bids: [
      { price: String(bid), size: "120" },
      { price: String(bid - 100), size: "80" },
      { price: String(bid - 200), size: "200" },
    ],
    asks: [
      { price: String(ask), size: "90" },
      { price: String(ask + 100), size: "150" },
      { price: String(ask + 200), size: "70" },
    ],
    generated_at: "",
  }) as unknown as DepthSnapshot;

const multiPosPage: PageInfo = { total: 4, limit: MARKET_PAGE, offset: 0, has_more: false };
const MULTI_POSITIONS = [
  { position_id: "mp1", market_id: "t1", outcome_id: "t1y", direction: "LONG", quantity: "12", avg_price: "500", realized_pnl: "0", status: "OPEN", amount_precision: 0, opened_at: new Date(Date.now() - 5 * 86_400_000).toISOString() },
  { position_id: "mp2", market_id: "t1", outcome_id: "t1n", direction: "LONG", quantity: "5", avg_price: "7500", realized_pnl: "0", status: "OPEN", amount_precision: 0, opened_at: new Date(Date.now() - 4 * 86_400_000).toISOString() },
  { position_id: "mp3", market_id: "t2", outcome_id: "t2y", direction: "LONG", quantity: "7", avg_price: "1500", realized_pnl: "0", status: "OPEN", amount_precision: 0, opened_at: new Date(Date.now() - 3 * 86_400_000).toISOString() },
  { position_id: "mp4", market_id: "t3", outcome_id: "t3y", direction: "LONG", quantity: "10", avg_price: "1200", realized_pnl: "0", status: "REDEEMABLE", redeemable_payout: "100000", winning_outcome_id: "t3y", amount_precision: 0, opened_at: new Date(Date.now() - 6 * 86_400_000).toISOString() },
] as unknown as PositionItem[];

const multiOrdPage: PageInfo = { total: 3, limit: MARKET_PAGE, offset: 0, has_more: false };
const MULTI_ORDERS = [
  { order_id: "mo1", market_id: "t1", outcome_id: "t1y", side: "buy", price: "1500", intent_price: "1500", quantity: "12", remaining_quantity: "12", filled_quantity: "0", tif: "GTC", status: "OPEN", amount_precision: 0, created_at: new Date(Date.now() - 3_600_000).toISOString() },
  { order_id: "mo2", market_id: "t1", outcome_id: "t1n", side: "buy", price: "7000", intent_price: "7000", quantity: "8", remaining_quantity: "5", filled_quantity: "3", tif: "GTD", status: "PARTIALLY_FILLED", expire_at: new Date(Date.now() + 3 * 86_400_000).toISOString(), amount_precision: 0, created_at: new Date(Date.now() - 7_200_000).toISOString() },
  { order_id: "mo3", market_id: "t3", outcome_id: "t3n", side: "buy", price: "2500", intent_price: "2500", quantity: "4", remaining_quantity: "4", filled_quantity: "0", tif: "GTC", status: "OPEN", amount_precision: 0, created_at: new Date(Date.now() - 1_800_000).toISOString() },
] as unknown as OrderItem[];

const multiHistPage: PageInfo = { total: 1, limit: 50, offset: 0, has_more: false };
const MULTI_ACTIVITY = [
  { key: "mh1", source: "position_event", kind: "INCREASED", activity: "buy", market_id: "t1", outcome_id: "t1y", delta_qty: "12", fill_price: "500", ref_id: "mp1", occurred_at: new Date(Date.now() - 3 * 86_400_000).toISOString() },
] as unknown as PortfolioActivityItem[];

export const Route = createFileRoute("/detail-preview")({
  head: () => buildHead({ path: "/detail-preview", title: "Detail preview (dev)", noindex: true }),
  // Pass the trade-intent params through (the mobile choice view pushes them
  // onto THIS route in the preview), plus the story switch + graph range.
  validateSearch: (
    s: Record<string, unknown>,
  ): { multi?: boolean; unpublished?: boolean; range?: string; trade?: string; side?: "buy" | "sell"; pos?: number } => ({
    multi: s.multi === true || s.multi === "true" || s.multi === 1 || s.multi === "1" ? true : undefined,
    // SM-322 story: the same binary market taken out of the marketplace.
    // Read-only banner, no trade panel, no Sell on the held position.
    unpublished:
      s.unpublished === true || s.unpublished === "true" || s.unpublished === 1 || s.unpublished === "1"
        ? true
        : undefined,
    range: typeof s.range === "string" ? s.range : undefined,
    trade: typeof s.trade === "string" ? s.trade : undefined,
    side: s.side === "sell" || s.side === "buy" ? s.side : undefined,
    pos: s.pos == null ? undefined : Number.isFinite(Number(s.pos)) ? Number(s.pos) : undefined,
  }),
  component: Preview,
});

// Seed the multi-outcome story: same pin-then-set pattern as the binary
// story below, over the 5-team catalog. Depth seeds use the exact key the
// OrderBook computes — keys.depth(id, 7, frame) — for BOTH frames (no
// quote-service here to serve the pre-flipped No book).
function seedMulti(qc: QueryClient, unpublished = false) {
  // ?multi=1&unpublished=1 — the answer-list flavor of the read-only story
  // (SM-322): banner, price chips instead of Yes/No buy pills, rows still
  // expandable to their book / graph.
  const group: MarketGroup = unpublished
    ? {
        ...MULTI_GROUP,
        effective_published: false,
        markets: MULTI_GROUP.markets.map((m) => ({ ...m, effective_published: false })),
      }
    : MULTI_GROUP;
  const entries: [readonly unknown[], unknown][] = [
    [keys.me(), { user_id: "u1", email: "dev@local", role: "user" }],
    [keys.preferences(), { preferences: { [PREF_ONE_CLICK_SELL_ENABLED]: true } }],
    [keys.marketDetail("e1"), group],
    [keys.marketGroups(), [group]],
    [keys.tickers(MULTI_IDS), MULTI_TICKERS],
    ...TEAMS.flatMap(({ mid, ask, bid }): [readonly unknown[], unknown][] => [
      [keys.depth(mid, 7, "A"), multiDepth(mid, Number(ask), Number(bid))],
      [keys.depth(mid, 7, "B"), multiDepth(mid, 10_000 - Number(bid), 10_000 - Number(ask))],
      [
        openOrdersForMarketQuery(mid).queryKey,
        {
          items: MULTI_ORDERS.filter((o) => o.market_id === mid),
          page: { total: 0, limit: OPEN_ORDERS_SCAN, offset: 0, has_more: false },
        },
      ],
    ]),
    [keys.walletBalance(), { available: "737500", reserved: "0", pending_withdrawal: "0" }],
    [keys.portfolio(), { items: MULTI_POSITIONS, page: multiPosPage }],
    [keys.portfolioFeed(MARKET_PAGE, { marketIds: MULTI_IDS, sort: POS_SORT }), { pages: [{ items: MULTI_POSITIONS, page: multiPosPage }], pageParams: [0] }],
    [[...keys.portfolio(), "event-chips", "e1"], { items: MULTI_POSITIONS, page: multiPosPage }],
    [[...keys.orders(), "event-chips", "e1"], { items: MULTI_ORDERS, page: multiOrdPage }],
    [keys.ordersFeed(MARKET_PAGE, { marketIds: MULTI_IDS, sort: ORDER_SORT }), { pages: [{ items: MULTI_ORDERS, page: multiOrdPage }], pageParams: [0] }],
    [keys.portfolioActivityFeed(MARKET_PAGE, { bucket: "all", marketIds: MULTI_IDS, sort: HIST_SORT }), { pages: [{ items: MULTI_ACTIVITY, page: multiHistPage }], pageParams: [0] }],
  ];
  // tickersPrefix pins every id-set ticker key the components compute.
  for (const k of [keys.tickersPrefix(), ...entries.map(([k]) => k)]) {
    qc.setQueryDefaults(k as unknown[], { staleTime: Infinity, gcTime: Infinity, retry: false, refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
  }
  for (const [k, v] of entries) qc.setQueryData(k as unknown[], v);
}

function Preview() {
  const { multi, unpublished, trade, side, pos } = Route.useSearch();
  const qc = useQueryClient();
  useState(() => {
    if (multi) {
      seedMulti(qc, !!unpublished);
      return true;
    }
    // A held position on m1 (Yes, 37 shares) so the Position tab + Sell render
    // (sell-seed pre-fills to 37), plus a winning (REDEEMABLE) + losing (LOST)
    // holding so the shared Redeem (win) / Close (loss) result cards show.
    const positions = [
      { position_id: "pp1", market_id: "m1", outcome_id: "y", direction: "LONG", quantity: "37", avg_price: "5000", realized_pnl: "0", status: "OPEN", amount_precision: 0, opened_at: new Date(Date.now() - 5 * 86_400_000).toISOString() },
      { position_id: "pp2", market_id: "m1", outcome_id: "y", direction: "LONG", quantity: "20", avg_price: "5000", realized_pnl: "0", status: "REDEEMABLE", redeemable_payout: "200000", amount_precision: 0, opened_at: new Date(Date.now() - 6 * 86_400_000).toISOString() },
      { position_id: "pp3", market_id: "m1", outcome_id: "n", direction: "LONG", quantity: "15", avg_price: "4200", realized_pnl: "0", status: "LOST", amount_precision: 0, opened_at: new Date(Date.now() - 7 * 86_400_000).toISOString() },
      // A whale OPEN holding (20,000 tickets) so the panel's P&L badge is far
      // wider than the Avg/Now cluster — regression guard that the badge wraps
      // onto its own line instead of overlapping "Now". This panel is a narrow
      // right rail, so the overflow reproduces here even at desktop widths.
      { position_id: "pp4", market_id: "m1", outcome_id: "y", direction: "LONG", quantity: "20000", avg_price: "5000", realized_pnl: "0", status: "OPEN", amount_precision: 0, opened_at: new Date(Date.now() - 5 * 86_400_000).toISOString() },
    ] as unknown as PositionItem[];
    const posPage: PageInfo = { total: 4, limit: MARKET_PAGE, offset: 0, has_more: false };

    // Pin every mocked key (incl. the *Feed keys the activity panel reads via
    // useOffsetFeed) so a background refetch against the proxied backend (empty
    // for the fake m1 ids) can't clobber the seed. staleTime Infinity + no
    // refetch-on-mount keeps the mocks put.
    for (const k of [
      // tickersPrefix: defaults partial-match, so the stem pins every id-set
      // ticker key the components compute.
      keys.me(), keys.marketDetail("m1"), keys.marketGroups(), keys.tickersPrefix(),
      // OrderBook reads keys.depth(id, 7, frame) — pin/seed both render frames.
      keys.depth("m1", 7, "A"), keys.depth("m1", 7, "B"),
      keys.portfolio(), keys.walletBalance(),
      // The answer/holdings chips' event-scoped fetches.
      [...keys.portfolio(), "event-chips", "m1"], [...keys.orders(), "event-chips", "m1"],
      keys.portfolioFeed(MARKET_PAGE, { marketIds: ["m1"], sort: POS_SORT }),
      keys.ordersFeed(MARKET_PAGE, { marketIds: ["m1"], sort: ORDER_SORT }),
      keys.portfolioActivityFeed(MARKET_PAGE, { bucket: "all", marketIds: ["m1"], sort: HIST_SORT }),
      openOrdersForMarketQuery("m1").queryKey,
      keys.preferences(),
    ]) {
      qc.setQueryDefaults(k, { staleTime: Infinity, gcTime: Infinity, retry: false, refetchOnMount: false, refetchOnWindowFocus: false, refetchOnReconnect: false });
    }
    qc.setQueryData(keys.me(), { user_id: "u1", email: "dev@local", role: "user" });
    // 1-Click Sell on so the panel's Position tab renders the in-place button.
    qc.setQueryData(keys.preferences(), { preferences: { [PREF_ONE_CLICK_SELL_ENABLED]: true } });
    // ?unpublished=1 flips only the cascade verdict — same market, same
    // holdings, so the story isolates exactly what the read-only mode
    // changes (SM-322). The flag rides the group AND the market: the page
    // reads the group's, the activity panel's Sell reads the market's.
    const group: MarketGroup = unpublished
      ? {
          ...MOCK_GROUP,
          effective_published: false,
          markets: MOCK_GROUP.markets.map((m) => ({ ...m, effective_published: false })),
        }
      : MOCK_GROUP;
    qc.setQueryData(keys.marketDetail("m1"), group);
    qc.setQueryData(keys.marketGroups(), [group]);
    qc.setQueryData(keys.tickers(["m1"]), [MOCK_TICKER]);
    qc.setQueryData(keys.depth("m1", 7, "A"), MOCK_DEPTH);
    qc.setQueryData(keys.depth("m1", 7, "B"), MOCK_DEPTH);
    qc.setQueryData(keys.walletBalance(), { available: "737500", reserved: "0", pending_withdrawal: "0" });
    // Positions: the plain key (hero / other consumers) + the panel's offset-feed key.
    qc.setQueryData(keys.portfolio(), { items: positions, page: posPage });
    qc.setQueryData(keys.portfolioFeed(MARKET_PAGE, { marketIds: ["m1"], sort: POS_SORT }), { pages: [{ items: positions, page: posPage }], pageParams: [0] });
    // The holdings chips' event-scoped fetches (rail-book chips on binary).
    qc.setQueryData([...keys.portfolio(), "event-chips", "m1"], { items: positions, page: posPage });
    qc.setQueryData([...keys.orders(), "event-chips", "m1"], { items: MOCK_ORDERS, page: ordersPage });
    // Open Order + History feeds (InfiniteData shape — useOffsetFeed).
    qc.setQueryData(keys.ordersFeed(MARKET_PAGE, { marketIds: ["m1"], sort: ORDER_SORT }), { pages: [{ items: MOCK_ORDERS, page: ordersPage }], pageParams: [0] });
    // The trade panel's per-market active-orders scan (SELL reservation netting).
    qc.setQueryData(openOrdersForMarketQuery("m1").queryKey, { items: MOCK_ORDERS, page: { total: MOCK_ORDERS.length, limit: OPEN_ORDERS_SCAN, offset: 0, has_more: false } });
    qc.setQueryData(keys.portfolioActivityFeed(MARKET_PAGE, { bucket: "all", marketIds: ["m1"], sort: HIST_SORT }), { pages: [{ items: MOCK_ACTIVITY, page: histPage }], pageParams: [0] });
    return true;
  });

  // Toggle simulates the portfolio → /markets/$id?side=sell URL intent (prop path).
  const [seed, setSeed] = useState(false);

  // Drops the same write useOrderStatusStream makes when an engine CANCELED
  // lands, so the trade panel's inline VerdictAlert (title — reason + hint,
  // dismiss X) can be exercised without a backend. Clearing goes through the
  // strip's own dismiss button — that's part of what the preview verifies.
  const seedVerdict = () =>
    qc.setQueryData<OrderVerdict>(keys.orderVerdict("m1"), {
      orderId: "o-preview",
      kind: "danger",
      title: "Order canceled",
      text: "Self-match prevented",
      hint: "It would have matched your own resting order on this market — cancel that open order to trade against the rest of the book.",
    });

  // The URL trade params (written by the mobile choice-view push) take
  // precedence — exactly how the real detail routes wire tradeIntent.
  const urlIntent = trade ? { marketId: trade, pos: pos ?? 0, side: side ?? ("buy" as const) } : undefined;

  return (
    <div className="min-h-dvh bg-bg px-6 text-fg">
      <div className="flex items-center gap-3 py-3">
        <span className="text-sm text-fg-muted">Dev preview · MarketDetailView</span>
        {/* Story switch — full reloads: the cache seeds run once per mount. */}
        <a href="/detail-preview" className="rounded-md bg-surface-2 px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-3">
          Binary
        </a>
        <a href="/detail-preview?multi=1" className="rounded-md bg-surface-2 px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-3">
          Multi
        </a>
        <a href="/detail-preview?unpublished=1" className="rounded-md bg-surface-2 px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-3">
          Unpublished
        </a>
        {!multi && (
          <>
            <button
              type="button"
              onClick={() => setSeed((s) => !s)}
              className="rounded-md bg-surface-2 px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-3"
            >
              {seed ? "Clear" : "Simulate"} sell intent (?side=sell)
            </button>
            <button
              type="button"
              onClick={seedVerdict}
              className="rounded-md bg-surface-2 px-3 py-1.5 text-xs font-medium text-fg hover:bg-surface-3"
            >
              Seed engine verdict
            </button>
          </>
        )}
      </div>
      <MarketDetailView
        id={multi ? "e1" : "m1"}
        tradeIntent={urlIntent ?? (seed ? { marketId: "m1", pos: 0, side: "sell" } : undefined)}
      />
    </div>
  );
}
